// Client-side password policy — MUST mirror the server policy in
// Server/src/utils/password_policy.py so users see requirements up front
// instead of being rejected only after submitting.
import { z } from "zod";

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

interface PasswordRule {
  label: string;      // short label for the requirements checklist
  message: string;    // full message (matches the server) when the rule fails
  test: (pw: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
    test: (pw) => pw.length >= PASSWORD_MIN_LENGTH,
  },
  {
    label: "One uppercase letter",
    message: "Password must contain at least one uppercase letter",
    test: (pw) => /[A-Z]/.test(pw),
  },
  {
    label: "One lowercase letter",
    message: "Password must contain at least one lowercase letter",
    test: (pw) => /[a-z]/.test(pw),
  },
  {
    label: "One number",
    message: "Password must contain at least one number",
    test: (pw) => /\d/.test(pw),
  },
  {
    label: "One special character",
    message: "Password must contain at least one special character",
    test: (pw) => /[^A-Za-z0-9]/.test(pw),
  },
];

/** Returns the first unmet-rule message (server order), or null if the password is valid. */
export function validatePassword(pw: string): string | null {
  for (const rule of PASSWORD_RULES) {
    if (!rule.test(pw)) return rule.message;
  }
  if (pw.length > PASSWORD_MAX_LENGTH) {
    return `Password must be at most ${PASSWORD_MAX_LENGTH} characters`;
  }
  return null;
}

/** Per-rule status for a live requirements checklist. */
export function getPasswordChecklist(pw: string): { label: string; met: boolean }[] {
  return PASSWORD_RULES.map((rule) => ({ label: rule.label, met: rule.test(pw) }));
}

/** Zod schema enforcing the full policy (single clear message, matching the server). */
export const zPassword = z.string().superRefine((pw, ctx) => {
  const error = validatePassword(pw);
  if (error) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
  }
});
